import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Calendar, Star, Clock, Award, BookOpen } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import MentorScheduleViewer from './MentorScheduleViewer';
import { MentorReviews } from './MentorReviews';
import { formatTime12Hour } from '@/lib/utils';

interface MentorProfileModalProps {
  mentorId: string | null;
  onClose: () => void;
  onBookSession: (mentor: any) => void;
}

const MentorProfileModal: React.FC<MentorProfileModalProps> = ({
  mentorId,
  onClose,
  onBookSession
}) => {
  const [mentor, setMentor] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<any>(null);

  useEffect(() => {
    if (mentorId) {
      fetchMentorDetails();
    }
  }, [mentorId]);

  const fetchMentorDetails = async () => {
    if (!mentorId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('mentors_public')
        .select(`
          *,
          mentor_availability (
            id,
            day_of_week,
            start_time,
            end_time,
            is_active
          )
        `)
        .eq('id', mentorId)
        .single();

      if (error) throw error;
      setMentor(data);
    } catch (error) {
      console.error('Error fetching mentor details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!mentorId) return null;

  const handleBookSession = () => {
    if (mentor && selectedSlot) {
      onBookSession({ ...mentor, selectedSlot });
      onClose();
    } else if (mentor) {
      // If no slot selected, still allow booking (will prompt to select time later)
      onBookSession(mentor);
      onClose();
    }
  };

  return (
    <Dialog open={!!mentorId} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-3">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-primary font-medium text-lg">
                {mentor?.first_name?.[0] || 'M'}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold">
                {mentor?.first_name} {mentor?.last_name}
              </h2>
              <p className="text-muted-foreground">{mentor?.specialty}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading mentor profile...</div>
          </div>
        ) : mentor ? (
          <div className="space-y-6">
            {/* Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <Star className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
                  <div className="text-2xl font-bold">{mentor.rating || 0}</div>
                  <p className="text-sm text-muted-foreground">Rating</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <BookOpen className="w-6 h-6 mx-auto mb-2 text-blue-500" />
                  <div className="text-2xl font-bold">{mentor.total_sessions || 0}</div>
                  <p className="text-sm text-muted-foreground">Sessions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Award className="w-6 h-6 mx-auto mb-2 text-mentor-primary" />
                  <div className="text-2xl font-bold">{mentor.years_experience || 0}</div>
                  <p className="text-sm text-muted-foreground">Years Experience</p>
                </CardContent>
              </Card>
            </div>

            {/* Bio */}
            <div>
              <h3 className="text-lg font-semibold mb-3">About</h3>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                {mentor.bio}
              </p>
            </div>

            {/* Expertise */}
            <div>
              <h3 className="text-lg font-semibold mb-3">Expertise</h3>
              <div className="flex flex-wrap gap-2">
                {(mentor.expertise || []).map((skill: string, index: number) => (
                  <Badge key={index} variant="secondary">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Hourly Rate */}
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <h3 className="font-semibold">Hourly Rate</h3>
                <p className="text-sm text-muted-foreground">Per session cost</p>
              </div>
              <div className="text-2xl font-bold text-primary">
                ${ (mentor.hourly_rate / 100).toFixed(0) }/hr
              </div>
            </div>


             {/* Note: Credits section removed for security - not exposed in public view */}

             <Separator />

            {/* Interactive Schedule Selector */}
            {mentor.id && (
              <MentorScheduleViewer
                mentorId={mentor.id}
                onTimeSlotSelect={setSelectedSlot}
                selectedSlot={selectedSlot}
              />
            )}

            <Separator />

            {/* Reviews Section */}
            <div>
              <MentorReviews 
                mentorId={mentor.id}
                averageRating={mentor.rating || 0}
                totalReviews={mentor.total_sessions || 0}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4">
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
              <Button onClick={handleBookSession} className="flex items-center" disabled={!selectedSlot}>
                <Clock className="w-4 h-4 mr-2" />
                {selectedSlot ? `Book Selected Time (${(mentor.hourly_rate / 100).toFixed(0)} Tokens)` : 'Select a Time Slot'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Mentor not found
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MentorProfileModal;